import { useSugarReadings7Days } from '../hooks/useSugarReadings';
import { FileText, HelpCircle, ChevronDown } from 'lucide-react';

interface GlucoseStats {
  cgmCoverage: number;
  monitoringFrequency: number;
  eHbA1c: number;
  mbg: number;
  timeInRange: {
    high: number;
    normal: number;
    low: number;
  };
}

function getValue(r: any): number {
  return Number(r?.value ?? r?.reading_value ?? r?.reading ?? 0);
}

function getTime(r: any): string {
  return r?.readingTime ?? r?.reading_time ?? r?.createdAt ?? '';
}

export function GlucoseTrends() {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 7);

  const _r = useSugarReadings7Days();
  const result = _r != null && typeof _r === 'object' ? _r : { data: undefined, isLoading: true };
  const readingsData = result.data;
  const isLoading = result?.isLoading ?? false;

  const readings = Array.isArray(readingsData) ? readingsData : readingsData?.data ?? [];
  const dateRange = `${startDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${endDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;

  const stats: GlucoseStats = {
    cgmCoverage: 0,
    monitoringFrequency: 0,
    eHbA1c: 0,
    mbg: 0,
    timeInRange: { high: 0, normal: 100, low: 0 },
  };

  if (readings.length > 0) {
    const totalReadings = readings.length;
    const avgGlucose = readings.reduce((sum, r) => sum + getValue(r), 0) / totalReadings;
    stats.eHbA1c = Number(((avgGlucose + 46.7) / 28.7).toFixed(1));
    stats.mbg = Math.round(avgGlucose);
    stats.monitoringFrequency = totalReadings;
    stats.cgmCoverage = Math.round((totalReadings / (7 * 288)) * 100);

    const inRange = readings.filter(r => {
      const v = getValue(r);
      return v >= 70 && v <= 180;
    }).length;
    const high = readings.filter(r => getValue(r) > 180).length;
    const low = readings.filter(r => getValue(r) < 70).length;

    stats.timeInRange = {
      high: Number(((high / totalReadings) * 100).toFixed(1)),
      normal: Number(((inRange / totalReadings) * 100).toFixed(1)),
      low: Number(((low / totalReadings) * 100).toFixed(1)),
    };
  }

  const StatCard = ({
    label,
    value,
    unit,
    showInfo,
  }: {
    label: string;
    value: number;
    unit?: string;
    showInfo?: boolean;
  }) => (
    <div className="bg-white rounded-xl p-6 border-2 border-dropsugar-blue/20">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-600 text-sm">{label}</span>
        {showInfo && <HelpCircle className="w-4 h-4 text-gray-400" />}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-4xl font-bold text-gray-900">{value}</span>
        {unit && <span className="text-gray-600 text-lg">{unit}</span>}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-dropsugar-green/10 to-dropsugar-blue/10 rounded-2xl p-6 border-2 border-dropsugar-green/30 animate-pulse">
        <div className="h-8 bg-white rounded w-1/4 mb-4"></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="h-24 bg-white rounded"></div>
          <div className="h-24 bg-white rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-dropsugar-green/10 to-dropsugar-blue/10 rounded-2xl p-6 border-2 border-dropsugar-green/30">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Trends</h2>
        <button className="p-2 hover:bg-white rounded-lg transition-colors">
          <FileText className="w-6 h-6 text-gray-700" />
        </button>
      </div>

      <button className="w-full flex items-center justify-center gap-2 mb-6 py-3 bg-white border-2 border-dropsugar-green/30 rounded-lg hover:bg-dropsugar-green/5 transition-colors">
        <span className="text-dropsugar-green font-medium">{dateRange}</span>
        <ChevronDown className="w-4 h-4 text-dropsugar-green" />
      </button>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <StatCard label="CGM Coverage Time" value={stats.cgmCoverage} unit="%" />
        <StatCard label="Monitoring Frequency" value={stats.monitoringFrequency} />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <StatCard label="eHbA1c" value={stats.eHbA1c} unit="%" showInfo />
        <StatCard label="MBG" value={stats.mbg} unit="mg/dL" showInfo />
      </div>

      <div className="bg-white rounded-xl p-6 border-2 border-dropsugar-blue/20">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-gray-900 font-medium">Time spent within the glucose target range (TIR).</span>
          <HelpCircle className="w-4 h-4 text-gray-400" />
        </div>

        <div className="relative mb-6">
          <div className="w-48 h-48 mx-auto">
            <svg viewBox="0 0 200 200" className="transform -rotate-90">
              <circle cx="100" cy="100" r="80" fill="none" stroke="#e5e7eb" strokeWidth="24" />
              <circle
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke="#72a83f"
                strokeWidth="24"
                strokeDasharray={`${(stats.timeInRange.normal / 100) * 502.65} 502.65`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold text-gray-900">{stats.timeInRange.normal.toFixed(1)}%</span>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-400"></div>
              <span className="text-sm text-gray-700">High:</span>
            </div>
            <div className="text-right">
              <div className="text-gray-900 font-medium">{stats.timeInRange.high.toFixed(1)}%</div>
              <div className="text-xs text-gray-600">&gt; 180 mg/dL</div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-400"></div>
              <span className="text-sm text-gray-700">Normal:</span>
            </div>
            <div className="text-right">
              <div className="text-gray-900 font-medium">{stats.timeInRange.normal.toFixed(1)}%</div>
              <div className="text-xs text-gray-600">70 - 180 mg/dL</div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-400"></div>
              <span className="text-sm text-gray-700">Low:</span>
            </div>
            <div className="text-right">
              <div className="text-gray-900 font-medium">{stats.timeInRange.low.toFixed(1)}%</div>
              <div className="text-xs text-gray-600">&lt; 70 mg/dL</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
